import React, { useState } from 'react';
import specialtiesData from '../data/SpecialtyOverview.json';
import mbtiData from '../data/MBTI_specialties.json';
import { 
  FaSearch, 
  FaStethoscope, 
  FaUserMd, 
  FaBookMedical,
  FaBriefcase, 
  FaClock, 
  FaGraduationCap,
  FaHandshake,
  FaArrowRight,
} from 'react-icons/fa';

const Specialties = () => {
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedMBTI, setSelectedMBTI] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSpecialtyClick = (specialty) => {
    setSelectedSpecialty(specialty);
  };

  const handleMBTIChange = (event) => {
    setSelectedMBTI(event.target.value);
  };

  const filteredSpecialties = specialtiesData.specialties
    .filter(specialty => specialty.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const renderSpecialtyDetails = (specialty) => (
    <div className="specialty-details" key={specialty.name}>
      <div className="specialty-header">
        <FaStethoscope className="specialty-header-icon" />
        <h3>{specialty.name}</h3>
      </div>
      
      <div className="info-grid">
        <div className="info-item">
          <div className="info-icon">
            <FaBriefcase />
          </div>
          <div className="info-content">
            <h4>Core Training Options</h4>
            <p>
              {Array.isArray(specialty.additional_info.core_training_options)
                ? specialty.additional_info.core_training_options.join(', ')
                : 'N/A'}
            </p>
          </div>
        </div>
        
        <div className="info-item">
          <div className="info-icon">
            <FaClock />
          </div>
          <div className="info-content">
            <h4>Indicative Length of Training</h4>
            <p>{specialty.additional_info.indicative_length_of_training || 'N/A'}</p>
          </div>
        </div>
        
        <div className="info-item">
          <div className="info-icon">
            <FaGraduationCap />
          </div>
          <div className="info-content">
            <h4>Exams</h4>
            <p>{specialty.additional_info.exams || 'N/A'}</p>
          </div>
        </div>
        
        <div className="info-item">
          <div className="info-icon">
            <FaBookMedical />
          </div>
          <div className="info-content">
            <h4>Approved Sub-Specialties</h4>
            <p>
              {Array.isArray(specialty.additional_info.approved_sub_specialties)
                ? specialty.additional_info.approved_sub_specialties.join(', ')
                : 'N/A'}
            </p>
          </div>
        </div>
        
        <div className="info-item">
          <div className="info-icon">
            <FaHandshake />
          </div>
          <div className="info-content">
            <h4>Approved Dual CCT Pairings</h4>
            <p>
              {Array.isArray(specialty.additional_info.approved_dual_CCT_pairings)
                ? specialty.additional_info.approved_dual_CCT_pairings.join(', ')
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="specialties-page">
      <div className="page-header">
        <h1>Medical Specialties</h1>
        <p className="subtitle">Explore detailed information about medical specialties and their training requirements</p>
      </div>

      <div className="content-container">
        <section className="explorer-section">
          <div className="section-header">
            <FaStethoscope className="section-icon" />
            <h2>Specialty Explorer</h2>
          </div>

          <div className="specialty-explorer">
            <div className="specialty-list-container">
              <div className="search-bar">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search specialties..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search specialties"
                />
              </div>
              
              <div className="scrollable-menu">
                {filteredSpecialties.length > 0 ? (
                  filteredSpecialties.map((specialty, index) => (
                    <button
                      key={index}
                      className={`specialty-item ${selectedSpecialty?.name === specialty.name ? 'selected' : ''}`}
                      onClick={() => handleSpecialtyClick(specialty)}
                    >
                      <FaStethoscope className="specialty-icon" />
                      <span>{specialty.name}</span>
                    </button>
                  ))
                ) : (
                  <div className="no-results">No specialties found matching "{searchTerm}"</div>
                )}
              </div>
            </div>

            <div className="specialty-details-container">
              {selectedSpecialty ? (
                renderSpecialtyDetails(selectedSpecialty)
              ) : (
                <div className="select-prompt">
                  <FaStethoscope className="prompt-icon" />
                  <p>Select a specialty from the list to view details</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="mbti-section">
          <div className="section-header">
            <FaUserMd className="section-icon" />
            <h2>MBTI Personality Match</h2>
          </div>

          <div className="mbti-content">
            <div className="mbti-description">
              <p>Find specialties that match your Myers-Briggs Type Indicator personality type. This can provide insights into which medical specialties might align with your natural strengths and preferences.</p>
            </div>
            
            <div className="mbti-selector-container">
              <select
                value={selectedMBTI}
                onChange={handleMBTIChange}
                className="mbti-selector"
              >
                <option value="">Select MBTI Personality Type</option>
                {Object.keys(mbtiData).map((mbtiType, index) => (
                  <option value={mbtiType} key={index}>
                    {mbtiType}
                  </option>
                ))}
              </select>
            </div>

            {selectedMBTI && (
              <div className="mbti-results">
                <div className="mbti-type">
                  <h3>{mbtiData[selectedMBTI].type}</h3>
                  <p className="mbti-description">{mbtiData[selectedMBTI].description || 'No description available for this personality type.'}</p>
                </div>
                
                <h4 className="recommended-title">Recommended Specialties</h4>
                <div className="mbti-specialties-grid">
                  {mbtiData[selectedMBTI].specialties.map((specialtyName, index) => {
                    const specialty = specialtiesData.specialties.find(
                      (s) => s.name === specialtyName
                    );
                    return specialty ? (
                      <div 
                        key={index} 
                        className="mbti-specialty-card"
                        onClick={() => setSelectedSpecialty(specialty)}
                      >
                        <FaStethoscope className="specialty-card-icon" />
                        <span>{specialty.name}</span>
                        <FaArrowRight className="card-arrow" />
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      
      <style jsx>{`
        .specialties-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 80px 24px 60px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #2d3748;
        }

        .page-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .page-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 16px;
          background: linear-gradient(135deg, #4361ee, #3a0ca3);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .subtitle {
          font-size: 1.125rem;
          color: #718096;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .content-container {
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        section {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px 24px;
          background: linear-gradient(to right, #f7fafc, #edf2f7);
          border-bottom: 1px solid #e2e8f0;
        }

        .section-icon {
          color: #4361ee;
          font-size: 1.25rem;
        }

        .section-header h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #2d3748;
        }

        /* Specialty Explorer Section */
        .specialty-explorer {
          display: flex;
          gap: 24px;
          padding: 24px;
        }

        .specialty-list-container {
          flex: 0 0 300px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .search-bar {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid #e2e8f0;
          background-color: #f8fafc;
        }

        .search-icon {
          color: #a0aec0;
          margin-right: 12px;
        }

        .search-bar input {
          border: none;
          outline: none;
          width: 100%;
          background: transparent;
          font-size: 0.9rem;
          color: #4a5568;
        }

        .scrollable-menu {
          height: 500px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 #f8fafc;
        }

        .scrollable-menu::-webkit-scrollbar {
          width: 6px;
        }

        .scrollable-menu::-webkit-scrollbar-track {
          background: #f8fafc;
        }

        .scrollable-menu::-webkit-scrollbar-thumb {
          background-color: #cbd5e0;
          border-radius: 3px;
        }

        .specialty-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 14px 16px;
          text-align: left;
          background: none;
          border: none;
          border-bottom: 1px solid #f0f3f8;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #4a5568;
        }

        .specialty-item:hover {
          background-color: #ebf8ff;
        }

        .specialty-item.selected {
          background-color: #ebf4ff;
          color: #4361ee;
          font-weight: 500;
          border-left: 4px solid #4361ee;
        }

        .specialty-icon {
          margin-right: 12px;
          color: #4361ee;
          font-size: 0.875rem;
        }

        .no-results {
          padding: 24px;
          text-align: center;
          color: #a0aec0;
          font-style: italic;
        }

        .specialty-details-container {
          flex: 1;
          padding: 24px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background-color: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          min-height: 400px;
        }

        .select-prompt {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 300px;
          color: #a0aec0;
          text-align: center;
          padding: 24px;
        }

        .prompt-icon {
          font-size: 3rem;
          margin-bottom: 20px;
          opacity: 0.5;
        }

        /* Specialty Details Styling */
        .specialty-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #ebf4ff;
        }

        .specialty-header-icon {
          color: #4361ee;
          font-size: 1.5rem;
        }

        .specialty-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #2d3748;
          word-break: break-word;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px 20px;
        }

        .info-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .info-icon {
          color: #4361ee;
          font-size: 1rem;
          padding-top: 3px;
          flex-shrink: 0;
        }

        .info-content {
          flex: 1;
          min-width: 0; /* Important for text overflow handling */
        }

        .info-content h4 {
          margin: 0 0 8px;
          font-size: 0.9rem;
          font-weight: 600;
          color: #4a5568;
        }

        .info-content p, .info-content a {
          margin: 0;
          font-size: 0.9rem;
          color: #718096;
          line-height: 1.5;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .truncate-link {
          display: inline-block;
          max-width: 100%;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        .truncate-link:hover {
          white-space: normal;
        }

        .info-content a {
          color: #4361ee;
          text-decoration: none;
        }

        .info-content a:hover {
          text-decoration: underline;
        }

        /* MBTI Section */
        .mbti-content {
          padding: 24px;
        }

        .mbti-description {
          margin-bottom: 20px;
          color: #718096;
          font-size: 0.95rem;
          line-height: 1.6;
        }

        .mbti-selector-container {
          margin-bottom: 24px;
        }

        .mbti-selector {
          width: 100%;
          max-width: 400px;
          padding: 12px 16px;
          font-size: 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #4a5568;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%234a5568' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: calc(100% - 16px) center;
          padding-right: 40px;
          background-size: 12px;
        }

        .mbti-selector:focus {
          outline: none;
          border-color: #4361ee;
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
        }

        .mbti-results {
          background: #f8fafc;
          border-radius: 12px;
          padding: 24px;
          border: 1px solid #e2e8f0;
        }

        .mbti-type {
          margin-bottom: 24px;
        }

        .mbti-type h3 {
          margin: 0 0 12px;
          font-size: 1.25rem;
          color: #2d3748;
        }

        .mbti-type p {
          margin: 0;
          color: #718096;
          font-size: 0.95rem;
          line-height: 1.6;
        }

        .recommended-title {
          margin: 0 0 16px;
          font-size: 1.1rem;
          color: #4a5568;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .mbti-specialties-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 16px;
        }

        .mbti-specialty-card {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          color: #4a5568;
          position: relative;
        }

        .mbti-specialty-card:hover {
          background-color: #ebf4ff;
          border-color: #4361ee;
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }

        .specialty-card-icon {
          color: #4361ee;
          font-size: 0.9rem;
          margin-right: 12px;
        }

        .card-arrow {
          position: absolute;
          right: 16px;
          color: #4361ee;
          opacity: 0;
          transition: opacity 0.3s ease, transform 0.3s ease;
        }

        .mbti-specialty-card:hover .card-arrow {
          opacity: 1;
          transform: translateX(3px);
        }

        /* Responsive adjustments */
        @media (max-width: 960px) {
          .info-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .specialties-page {
            padding: 60px 16px 40px;
          }

          .page-header {
            margin-bottom: 32px;
          }

          .page-header h1 {
            font-size: 2rem;
          }

          .specialty-explorer {
            flex-direction: column;
          }

          .specialty-list-container {
            flex: none;
            width: 100%;
          }

          .scrollable-menu {
            height: 300px;
          }

          .mbti-specialties-grid {
            grid-template-columns: 1fr;
          }
          
          .specialty-details-container {
            min-height: auto;
            padding: 16px;
          }

          .info-item {
            gap: 8px;
          }

          .specialty-header h3 {
            font-size: 1.25rem;
          }

          .info-content h4 {
            font-size: 0.85rem;
          }

          .info-content p, .info-content a {
            font-size: 0.85rem;
          }
        }

        @media (max-width: 480px) {
          .page-header h1 {
            font-size: 1.75rem;
          }

          .subtitle {
            font-size: 1rem;
          }

          .section-header {
            padding: 16px;
          }

          .specialty-explorer, 
          .mbti-content {
            padding: 16px;
          }

          .mbti-specialty-card span {
            font-size: 0.875rem;
          }

          .specialty-details-container {
            padding: 12px;
          }
          
          .specialty-header {
            padding-bottom: 12px;
            margin-bottom: 16px;
          }
          
          .info-grid {
            gap: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default Specialties;